// 客户管理与客户账号表单的草稿：输入框里的文本形态，提交时换算成 docs/04 第 2 节的写入体。
import type { Errors } from "@/app/useDraft";
import { CURRENCY_ERROR, formatMoneyInput, joinLines, parseMoney, parsePositive, splitLines, utf8Bytes, validCurrency } from "@/app/form";
import type { Account, AccountInput, ChargeOn, Customer, EntityStatus, Input, Protocol, TrafficType } from "@/app/master";

/** 客户草稿（docs/03 3.1）：币种是客户的账单币种；供应商不带币种，另有自己的草稿（决策 D85）。 */
export interface CustomerDraft { name: string; currency: string; status: EntityStatus; note: string; charge_on: ChargeOn; tier: string }

export function customerDraft(row?: Customer): CustomerDraft {
  return row
    ? { name: row.name, currency: row.currency, status: row.status, note: row.note ?? "", charge_on: row.charge_on, tier: row.tier }
    : { name: "", currency: "USD", status: "active", note: "", charge_on: "submitted", tier: "standard" };
}

export function customerInput(d: CustomerDraft): { body?: Input<Customer>; errors: Errors<CustomerDraft> } {
  const errors: Errors<CustomerDraft> = {};
  if (!d.name.trim()) errors.name = "填写客户名称。";
  if (!validCurrency(d.currency)) errors.currency = CURRENCY_ERROR;
  if (Object.keys(errors).length) return { errors };
  return { errors, body: { name: d.name.trim(), currency: d.currency, status: d.status, note: d.note.trim() || null, charge_on: d.charge_on, tier: d.tier.trim() } };
}

/** 客户账号口令规则，与服务端一致（决策 D162）：SMPP 3.4 的 password 是不超过 8 个八位字节的 C-Octet String，取 6 到 8 个 ASCII 可打印字符；HTTP 按字符数至少 8 个。 */
const SMPP_PASSWORD = /^[\x20-\x7e]{6,8}$/;
const HTTP_PASSWORD_MIN = 8;

/** 口令规则的提示与错误文案共用这一句。 */
export function passwordRule(protocol: Protocol): string {
  return protocol === "smpp" ? "SMPP 账号口令 6 到 8 个 ASCII 字符" : `HTTP 账号口令至少 ${HTTP_PASSWORD_MIN} 个字符`;
}

export interface AccountDraft {
  customer_id: string; name: string; protocol: Protocol; system_id: string; password: string;
  ip_whitelist: string; ip_unrestricted: boolean; tls_required: boolean; tps: string; max_binds: string;
  traffic_type: TrafficType; allowed_senders: string; credit_limit: string;
  pinned_channel_id: string; webhook_url: string; webhook_allow_http: boolean; status: EntityStatus; dlr_policy_id: string;
}

export function accountDraft(row?: Account): AccountDraft {
  if (!row) {
    return {
      customer_id: "", name: "", protocol: "smpp", system_id: "", password: "", ip_whitelist: "", ip_unrestricted: false, tls_required: false,
      tps: "10", max_binds: "2", traffic_type: "otp", allowed_senders: "", credit_limit: "0",
      pinned_channel_id: "", webhook_url: "", webhook_allow_http: false, status: "active", dlr_policy_id: "",
    };
  }
  return {
    customer_id: String(row.customer_id), name: row.name, protocol: row.protocol, system_id: row.system_id, password: "",
    ip_whitelist: joinLines(row.ip_whitelist), ip_unrestricted: row.ip_unrestricted, tls_required: row.tls_required,
    tps: String(row.tps), max_binds: String(row.max_binds), traffic_type: row.traffic_type, allowed_senders: joinLines(row.allowed_senders),
    credit_limit: formatMoneyInput(row.credit_limit),
    pinned_channel_id: row.pinned_channel_id == null ? "" : String(row.pinned_channel_id), webhook_url: row.webhook_url ?? "",
    dlr_policy_id: row.dlr_policy_id == null ? "" : String(row.dlr_policy_id),
    webhook_allow_http: row.webhook_allow_http, status: row.status,
  };
}

function passwordFits(protocol: Protocol, pw: string): boolean {
  return protocol === "smpp" ? SMPP_PASSWORD.test(pw) : [...pw].length >= HTTP_PASSWORD_MIN;
}

export function accountInput(d: AccountDraft, isNew: boolean): { body?: AccountInput; errors: Errors<AccountDraft> } {
  const errors: Errors<AccountDraft> = {};
  const tps = parsePositive(d.tps);
  const maxBinds = parsePositive(d.max_binds);
  const credit = parseMoney(d.credit_limit);
  if (!d.customer_id) errors.customer_id = "选择所属客户。";
  if (!d.name.trim()) errors.name = "填写账号名称。";
  if (!d.system_id.trim()) errors.system_id = "填写 system_id。";
  if (isNew && !d.password) errors.password = "新建账号必须设置口令。";
  else if (d.password && !passwordFits(d.protocol, d.password)) errors.password = `${passwordRule(d.protocol)}。`;
  else if (utf8Bytes(d.password) > 72) errors.password = "口令不超过 72 字节。";
  if (tps == null) errors.tps = "正整数。";
  if (maxBinds == null) errors.max_binds = "正整数。";
  if (credit == null || credit < 0) errors.credit_limit = "非负金额，最多 6 位小数。";
  if (!d.ip_unrestricted && !splitLines(d.ip_whitelist).length) errors.ip_whitelist = "填写 CIDR，或勾选不限制来源 IP。";
  if (Object.keys(errors).length || tps == null || maxBinds == null || credit == null) return { errors };
  return {
    errors,
    body: {
      customer_id: Number(d.customer_id), name: d.name.trim(), protocol: d.protocol, system_id: d.system_id.trim(), password: d.password,
      ip_whitelist: splitLines(d.ip_whitelist), ip_unrestricted: d.ip_unrestricted, tls_required: d.tls_required, tps, max_binds: maxBinds,
      traffic_type: d.traffic_type, allowed_senders: splitLines(d.allowed_senders), credit_limit: credit,
      pinned_channel_id: d.pinned_channel_id ? Number(d.pinned_channel_id) : null, webhook_url: d.webhook_url.trim() || null,
      dlr_policy_id: d.dlr_policy_id ? Number(d.dlr_policy_id) : null,
      webhook_allow_http: d.webhook_allow_http, status: d.status,
    },
  };
}
